import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import MDInput from "components/MDInput";
import httpClient from "api/httpClient";
import { compose } from "@mui/system";

const apiBase = process.env.REACT_APP_API_URL || "";

const DynamicAutocomplete = ({ col, row, index, handleUpdateRow, vKey, tKey }) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const hydratedRef = useRef(false);

  const currentId = row && typeof row === "object" ? row[col.id] : null;

  // Independent fetcher using your httpClient pattern
  const loadOptions = useCallback(
    async (search = "") => {
      if (!col.remoteUrl) return;
      try {
        setLoading(true);
        const url = `${apiBase}${col.remoteUrl}?page=0&size=20&q=${search}`;
        const res = await httpClient.get(url);
        const content = res.data.content || [];

        const formatted = content.map((item) => ({
          ...item,
          label: col.composeLabel ? col.composeLabel(item) : item[tKey] || "",
        }));

        setOptions((prev) => {
          const uniqueMap = new Map();
          // Keep existing selections in the list so labels don't disappear on re-render
          prev.forEach((opt) => uniqueMap.set(String(opt[vKey]), opt));
          formatted.forEach((opt) => uniqueMap.set(String(opt[vKey]), opt));
          return Array.from(uniqueMap.values());
        });
      } catch (err) {
        console.error(`Failed to load data for ${col.id}`, err);
      } finally {
        setLoading(false);
      }
    },
    [col.remoteUrl, col.id, tKey, vKey, col.composeLabel]
  );

  // Initial hydration: Fetch the specific record if a value exists
  useEffect(() => {
    if (currentId && !hydratedRef.current) {
      hydratedRef.current = true;
      loadOptions(currentId);
    }
  }, [currentId, loadOptions]);

  const selectedOption = options.find((opt) => String(opt[vKey]) === String(currentId)) || null;

  return (
    <Autocomplete
      sx={{ width: "100%" }}
      options={options}
      loading={loading}
      value={selectedOption}
      key={`${col.id}-${index}`}
      onOpen={() => {
        if (options.length === 0) loadOptions();
      }}
      getOptionLabel={(opt) => (opt && opt[tKey] ? String(opt[tKey]) : "")}
      isOptionEqualToValue={(option, val) => String(option?.[vKey]) === String(val?.[vKey])}
      onChange={(e, val) => {
        handleUpdateRow(index, col.id, val ? val[vKey] : null);
      }}
      onInputChange={(e, val, reason) => {
        if (reason === "input" && val.length >= 3) {
          loadOptions(val);
        }
      }}
      renderInput={(params) => (
        <MDInput
          {...params}
          label={col.label}
          variant="standard"
          fullWidth
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

DynamicAutocomplete.propTypes = {
  col: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string,
    remoteUrl: PropTypes.string,
    composeLabel: PropTypes.func,
  }).isRequired,
  row: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  handleUpdateRow: PropTypes.func.isRequired,
  vKey: PropTypes.string.isRequired,
  tKey: PropTypes.string.isRequired,
};

export default DynamicAutocomplete;
